import { z } from "zod";

import { bosuYokSay } from "./bosDeger";

/**
 * MÜVEKKİL KAYDI (CLOS-168 `[115]`).
 *
 * ### ⚠ Müvekkil, hesap ya da sözleşme tarafı DEĞİL
 *
 * Kart üç ayrımı birden çiziyor: müvekkil oluşturmak KULLANICI HESABI, TENANT, üyelik ya
 * da Müvekkil Temsilcisi ÜRETMEZ ve sözleşme tarafı kaydıyla aynı kavram değildir. Bu
 * şema o yüzden yalnız müvekkilin KENDİSİNİ tarif ediyor; ilişki `[116]`'nın işi.
 *
 * ### ⚠ Tür, alanları BELİRLİYOR
 *
 * Gerçek kişi ile kuruluşun kimlik alanları farklı ve karışmamalı: karışmış bir kayıtta
 * "bu müvekkil aslında ne?" sorusu veriden cevaplanamaz. Ayrım hem burada (ayrık birleşim)
 * hem veritabanı `CHECK`'inde.
 */

export const CLIENT_TYPES = ["individual", "organization"] as const;
export type ClientType = (typeof CLIENT_TYPES)[number];

export const CLIENT_TYPE_NAMES: Record<ClientType, string> = {
  individual: "Gerçek kişi",
  organization: "Kuruluş",
};

const iletisim = {
  /** İletişim alanları ZORUNLU DEĞİL: müvekkil kaydı bir iletişim defteri değil. */
  email: bosuYokSay(z.string().trim().email("Geçerli bir e-posta girin").max(200).nullish()),
  phone: bosuYokSay(z.string().trim().max(40, "Telefon en fazla 40 karakter").nullish()),
};

const gercekKisi = z.strictObject({
  clientType: z.literal("individual"),
  firstName: z.string().trim().min(1, "Ad zorunludur").max(100),
  lastName: z.string().trim().min(1, "Soyad zorunludur").max(100),
  /**
   * ⚠ Kimlik numarası HASSAS ve ZORUNLU DEĞİL. Kart *"veri minimizasyonu"* diyor: bir
   * müvekkili kaydetmek için kimlik numarası şart değil ve şart koşsaydık, gerekmediği
   * hâlde hassas veri toplardık.
   */
  nationalId: bosuYokSay(
    z
      .string()
      .trim()
      .regex(/^[0-9]{11}$/, "Kimlik numarası 11 haneli olmalıdır")
      .nullish(),
  ),
  ...iletisim,
});

const kurulus = z.strictObject({
  clientType: z.literal("organization"),
  legalName: z.string().trim().min(1, "Ticari unvan zorunludur").max(200),
  taxNumber: bosuYokSay(
    z
      .string()
      .trim()
      .regex(/^[0-9]{10,11}$/, "Vergi numarası 10 veya 11 haneli olmalıdır")
      .nullish(),
  ),
  taxOffice: bosuYokSay(z.string().trim().max(120).nullish()),
  ...iletisim,
});

/**
 * ⚠ Üyeler STRICT ve onay alanı üyelerin İÇİNDE. Ayrık birleşimi `.and()` ile genişletmek
 * kolay görünüyordu ama strictliği delerdi: gerçek kişiye `legalName` göndermek sessizce
 * düşer ve "bu müvekkil aslında ne?" sorusu yine bulanıklaşırdı. Test bunu ilk koşuda
 * yakaladı.
 */
/**
 * CLOSER MÜVEKKİL DAVETİ — OLUŞTURMAYLA AYNI İSTEKTE (CLOS-1122 / CLOS-1140, rapor §4.1).
 *
 * "Müvekkili oluştur ve davet gönder" TEK sunucu işlemidir: kayıt + hizmet ilişkisi daveti
 * aynı transaction'da. İki istek olsaydı "müvekkil var, davet yok" yarım durumu doğardı.
 * Gerçek kişide `targetEmail` müvekkilin KENDİ e-postası; kurumda ilk yetkilinin e-postası
 * (`contactName` yalnız iletişim kişisi — yetki/üyelik üretmez, Halit kararı S4).
 * Kapsam ZORUNLU ve varsayılansız (S3): sunucu politikaya göre yeniden doğrular.
 */
export const closerInviteSchema = z.strictObject({
  targetEmail: z.string().trim().email("Geçerli bir e-posta girin").max(200),
  scope: z.string().trim().min(1, "Hizmet kapsamı seçin"),
  scopeDetail: z.string().trim().max(1000).optional(),
  durationDays: z.number().int().positive().optional(),
  contactName: bosuYokSay(z.string().trim().max(200).nullish()),
});
export type CloserInviteInput = z.infer<typeof closerInviteSchema>;

export const createClientSchema = z.discriminatedUnion("clientType", [
  gercekKisi.extend({
    /**
     * OLASI YİNELENEN KAYIT ONAYI.
     *
     * ⚠ Kart: *"normalize kimlik/iletişim sinyalleri olası yinelenen kayıt UYARISI üretir;
     * OTOMATİK MERGE YAPILMAZ."* Uyarı sessizce geçilemiyor ama karar da kullanıcının:
     * aynı adı taşıyan iki farklı müvekkil gerçek bir durum ve sistemin onları
     * birleştirmesi geri alınamaz bir veri kaybı olurdu.
     */
    acknowledgeDuplicate: z.boolean().default(false),
    closerInvite: closerInviteSchema.optional(),
  }),
  kurulus.extend({
    acknowledgeDuplicate: z.boolean().default(false),
    closerInvite: closerInviteSchema.optional(),
  }),
]);
export type CreateClientInput = z.infer<typeof createClientSchema>;

/**
 * Listede gösterilen müvekkil.
 *
 * ⚠ KİMLİK ve VERGİ numarası YOK. Kart: *"gereksiz bilgi LİSTELERE, olaylara veya audit'e
 * çıkmaz."* Liste bir seçim yüzeyi; kimlik numarası orada hiçbir soruyu cevaplamıyor ama
 * ekranı gören herkese açılıyor olurdu.
 */
export type ClientListItem = {
  id: string;
  clientType: ClientType;
  /**
   * Kayıt gündemde mi arşivde mi (CLOS-169 `[116]`). Listede DÖNÜYOR çünkü arşiv süzgeci
   * seçildiğinde kullanıcı ekranda hangi kaydın neden geldiğini görebilmeli.
   */
  status: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  createdByName: string;
  createdAt: string;
};

/**
 * MÜVEKKİL ↔ CLOSER BAĞI durumu (CLOS-1107 · R3 §17.1 · K1).
 *
 * `not_linked` yerel kayıt, Closer kimliğine bağlı değil · `link_pending` e-postaya
 * gönderilmiş bağlantı daveti karar bekliyor · `linked` karşı tarafın açık kabulüyle
 * bir Closer organizasyonuna bağlanmış.
 *
 * ⚠ Bağ ERİŞİM DEĞİL: `linked` yalnız "hangi organizasyona denk geliyor" sorusunun
 * cevabı; sözleşme/not/kişisel veri erişimi her kayıt için ayrıca verilir.
 */
export const CLIENT_LINK_STATES = ["not_linked", "link_pending", "linked"] as const;
export type ClientLinkState = (typeof CLIENT_LINK_STATES)[number];

/**
 * TEMSİLİ / CLOSER MÜVEKKİL = kullanım biçimi, kolon değil (CLOS-1122 K1): Closer bağı ve
 * bekleyen davetten TÜRETİLİR. Yeni bir "mode" kolonu iki kaynak yaratırdı.
 */
export const CLIENT_CLOSER_MODES = ["representative", "closer_invited", "closer"] as const;
export type ClientCloserMode = (typeof CLIENT_CLOSER_MODES)[number];
export const CLIENT_CLOSER_MODE_NAMES: Record<ClientCloserMode, string> = {
  representative: "Temsili Müvekkil",
  closer_invited: "Closer Müvekkil · davet bekliyor",
  closer: "Closer Müvekkil",
};
export function closerModeOf(state: ClientLinkState): ClientCloserMode {
  if (state === "linked") return "closer";
  if (state === "link_pending") return "closer_invited";
  return "representative";
}

export const CLIENT_LINK_STATE_NAMES: Record<ClientLinkState, string> = {
  not_linked: "Closer'a bağlı değil",
  link_pending: "Davet bekliyor",
  linked: "Closer'a bağlı",
};

export type ClientCloserLink = {
  state: ClientLinkState;
  linkedTenantId: string | null;
  linkedTenantName: string | null;
  linkedAt: string | null;
  /** `link_pending` durumunda bekleyen hizmet ilişkisi talebinin kimliği. */
  pendingRequestId: string | null;
};

/** Detayda kimlik alanları da var — ayrı bir yüzey, ayrı bir karar. */
export type ClientDetail = ClientListItem & {
  firstName: string | null;
  lastName: string | null;
  nationalId: string | null;
  legalName: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
  /**
   * İyimser kilit sayacı (CLOS-170 `[117]`) — güncelleme isteğinde GERİ GÖNDERİLİYOR.
   *
   * ⚠ Sunucu "en son hâli" varsayarak yazsaydı, iki kişi aynı müvekkili düzenlediğinde
   * ikincinin kaydı ilkininkini sessizce ezerdi. Sürüm istemcide GÖRÜNMÜYOR ama forma
   * gömülüyor: kullanıcının okuduğu hâl ile yazdığı hâl aynı olmalı.
   */
  version: number;
  /** Son değişiklik anı — detayda "ne zaman güncellendi" sorusunun cevabı. */
  updatedAt: string;
  /**
   * Bu kayıt başka bir kayda birleştirildiyse HEDEFİN kimliği (CLOS-171 `[118]`).
   *
   * ⚠ Detay 404 DÖNMÜYOR: eski kimlikle gelen kullanıcı "kayıt yok" değil "bu kayıt şuraya
   * taşındı" cevabını almalı. Kartın kriteri: *"redirect/tombstone ve eski kimlik
   * izlenebilirliği korunur."*
   */
  mergedIntoId: string | null;
  /** Closer bağı (CLOS-1107): rozet ve "Closer'a davet et" düğmesi bu alandan okunuyor. */
  closerLink: ClientCloserLink;
};

/** Olası yineleme adayı — uyarıda gösterilen bilgi en aza indirilmiş. */
export type ClientDuplicateCandidate = {
  id: string;
  displayName: string;
  /** Hangi sinyal eşleşti: kimlik, e-posta ya da telefon. */
  matchedOn: "identity" | "email" | "phone";
};

export const CLIENT_DUPLICATE_CODE = "possible_duplicate";
export const CLIENT_DUPLICATE_MESSAGE =
  "Aynı bilgilerle eşleşen bir müvekkil var. Yine de yeni kayıt oluşturmak istiyor musunuz?";

/** Normalize sinyaller — karşılaştırma için; ekranda gösterilmiyor. */
export function normalizeClientSignal(value: string | null | undefined): string | null {
  if (!value) return null;
  const sade = value.trim().toLowerCase();
  return sade.length > 0 ? sade : null;
}

export function normalizeClientDigits(value: string | null | undefined): string | null {
  if (!value) return null;
  const sade = value.replace(/\D/g, "");
  return sade.length > 0 ? sade : null;
}

/**
 * TELEFON normalizasyonu — SON 10 HANE.
 *
 * ⚠ Yalnız rakamları almak yetmiyor: "0532 111 22 33" ile "+90 532 111 2233" aynı numara
 * ama farklı rakam dizisi üretiyor ve yineleme uyarısı ikisini ayrı sanardı. Ülke kodu ve
 * baştaki sıfır atılıyor; son on hane Türkiye'de numaranın kendisi. Test bu iki biçimi
 * karşılaştırıyor.
 */
export function normalizeClientPhone(value: string | null | undefined): string | null {
  const sade = normalizeClientDigits(value);
  if (!sade) return null;
  return sade.length > 10 ? sade.slice(-10) : sade;
}

/**
 * MÜVEKKİL GÜNCELLEME (CLOS-170 `[117]`).
 *
 * ### ⚠ TÜR DEĞİŞTİRİLEMEZ
 *
 * Şemada `clientType` YOK. Gerçek kişiyi kuruluşa çevirmek, kaydın kimliğini değiştirmek
 * demek: kimlik numarası taşınamaz, ticari unvan yoktan var olamaz ve veritabanındaki
 * "türe özgü alanlar karışmaz" `CHECK`'i ancak alanlar toptan silinerek sağlanabilirdi —
 * yani güncelleme sessizce bir veri kaybı olurdu. Yanlış türde açılmış bir kayıt için
 * doğru yol yeni kayıt + arşiv (`[119]`).
 *
 * ### ⚠ En az bir alan
 *
 * Boş bir gövde bir sürüm artırır, bir revizyon satırı ve bir audit kaydı üretir ama
 * hiçbir şeyi değiştirmezdi — geçmişi gürültüyle doldurmak, geçmişi okunamaz yapmanın
 * en sessiz yolu.
 */
export const updateClientSchema = z
  .strictObject({
    /** İSTEMCİNİN OKUDUĞU sürüm; sunucu bununla eşleşmezse 409 döner. */
    version: z.coerce.number().int().min(1),

    /** Gerçek kişi alanları — kuruluşta gönderilmesi 422. */
    firstName: z.string().trim().min(1, "Ad zorunludur").max(100).optional(),
    lastName: z.string().trim().min(1, "Soyad zorunludur").max(100).optional(),
    nationalId: z
      .string()
      .trim()
      .regex(/^[0-9]{11}$/, "Kimlik numarası 11 haneli olmalıdır")
      .nullish(),

    /** Kuruluş alanları — gerçek kişide gönderilmesi 422. */
    legalName: z.string().trim().min(1, "Ticari unvan zorunludur").max(200).optional(),
    taxNumber: z
      .string()
      .trim()
      .regex(/^[0-9]{10,11}$/, "Vergi numarası 10 veya 11 haneli olmalıdır")
      .nullish(),
    taxOffice: bosuYokSay(z.string().trim().max(120).nullish()),

    email: bosuYokSay(z.string().trim().email("Geçerli bir e-posta girin").max(200).nullish()),
    phone: bosuYokSay(z.string().trim().max(40, "Telefon en fazla 40 karakter").nullish()),
  })
  .refine((v) => Object.keys(v).some((k) => k !== "version"), {
    message: "En az bir alan güncellenmelidir",
  });
export type UpdateClientInput = z.infer<typeof updateClientSchema>;

/** Türü değiştirilemeyen kaydın reddi — mesaj tek yerde, uçlar aynı metni kullanıyor. */
export const CLIENT_TYPE_IMMUTABLE_MESSAGE =
  "Müvekkil türü değiştirilemez; yanlış türde açılmış kayıt arşivlenip yeniden oluşturulur";

/** Arşivdeki kayıt OKUNUR ama güncellenmez (kartın açık ayrımı). */
export const CLIENT_ARCHIVED_UPDATE_MESSAGE =
  "Arşivdeki müvekkil güncellenemez; önce yeniden aktifleştirilmelidir";

export const CLIENT_STALE_VERSION_MESSAGE =
  "Müvekkil siz düzenlerken başkası tarafından güncellendi";

/**
 * Bir alanın türe uygunluğu — sunucu ve arayüz AYNI tablodan okuyor.
 *
 * ⚠ İki yerde ayrı yazılsaydı, arayüz kuruluşa "Ad" alanı gösterip sunucudan 422 alırdı;
 * kullanıcı da neden reddedildiğini anlamazdı.
 */
export const CLIENT_TYPE_FIELDS: Record<ClientType, readonly string[]> = {
  individual: ["firstName", "lastName", "nationalId", "email", "phone"],
  organization: ["legalName", "taxNumber", "taxOffice", "email", "phone"],
};

/**
 * HASSAS alanlar — audit'e DEĞERİ değil yalnız DEĞİŞTİĞİ yazılıyor.
 *
 * ⚠ `[115]`: *"gereksiz bilgi listelere, olaylara veya AUDIT'e çıkmaz."* Önceki değer
 * kaybolmuyor; kiracı izolasyonu altındaki `client_revisions` tablosunda duruyor.
 */
export const CLIENT_SENSITIVE_FIELDS: readonly string[] = ["nationalId", "taxNumber"];

/**
 * MÜVEKKİL BİRLEŞTİRME (CLOS-171 `[118]`, ADR-0030).
 *
 * ### ⚠ Otomatik birleştirme YOK
 *
 * Kart: *"eşleşme sinyalleri yalnız uyarı/adayı üretir; AI veya sistem OTOMATİK MERGE
 * YAPAMAZ."* Bu yüzden istek hem KAYNAK hem HEDEF kaydı ve İKİSİNİN de sürümünü açıkça
 * taşıyor: karar kullanıcınındır ve kullanıcı hangi iki kaydın hangi hâlini gördüğünü
 * söylemek zorundadır.
 */
export const mergeClientsSchema = z.strictObject({
  /** Kapanacak (mezar taşına dönecek) kayıt. */
  sourceId: z.uuid(),
  /**
   * İki kaydın da OKUNAN sürümü.
   *
   * ⚠ Yalnız hedefin sürümü istenseydi, kaynak kayıt önizleme ile birleştirme arasında
   * değişebilir ve kullanıcı görmediği bir hâli kapatmış olurdu. Kart: *"merge sırasında
   * yeni yazma veya paralel merge yarışı version/concurrency kontrolüyle engellenir."*
   */
  sourceVersion: z.coerce.number().int().min(1),
  targetVersion: z.coerce.number().int().min(1),
});
export type MergeClientsInput = z.infer<typeof mergeClientsSchema>;

/** Önizlemede gösterilen taşınacak ilişki sınıfı. */
export type ClientMergeReferenceSummary = {
  key: string;
  label: string;
  /** Kaynakta bu sınıftan kaç referans var — hepsi hedefe taşınacak. */
  count: number;
};

/**
 * BİRLEŞTİRME ÖNİZLEMESİ — kart: *"geri alınamaz etkiler işlem öncesi gösterilir."*
 *
 * ⚠ Eşleşme bir SKOR taşımıyor (ADR-0030): yüzde, olmayan bir kesinlik iddia eder ve
 * kullanıcı düşünmeyi bırakır. Bunun yerine hangi SİNYALİN eşleştiği söyleniyor.
 */
export type ClientMergePreview = {
  source: { id: string; displayName: string; version: number };
  target: { id: string; displayName: string; version: number };
  /** Eşleşen sinyaller — yoksa boş; boş olması birleştirmeyi engellemiyor (karar kullanıcının). */
  matchedOn: ("identity" | "email" | "phone")[];
  references: ClientMergeReferenceSummary[];
  /** Kayıtlı adapter'ı olmayan ilişki sınıfları taşınmıyor; kullanıcı bunu ÖNCEDEN görüyor. */
  warnings: string[];
};

export const CLIENT_MERGE_SAME_RECORD_MESSAGE = "Bir müvekkil kendisiyle birleştirilemez";
export const CLIENT_MERGE_TYPE_MISMATCH_MESSAGE =
  "Gerçek kişi ile kuruluş birleştirilemez; farklı türdeki kayıtlar aynı kişi olamaz";
export const CLIENT_MERGE_ALREADY_MERGED_MESSAGE =
  "Bu müvekkil daha önce başka bir kayda birleştirilmiş";
export const CLIENT_MERGE_ARCHIVED_TARGET_MESSAGE =
  "Arşivdeki bir müvekkil birleştirme hedefi olamaz";
